import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/feature/Logo";
import WebinarPlayer from "@/components/feature/WebinarPlayer";
import { parsePhoneNumberFromString } from "libphonenumber-js/mobile";
import {
  trackProspectVisit,
  fetchPhoneStatus,
  updateWebinarPhone,
  verifyWebinarPhone,
  fetchWebinarConfig,
  getStoredToken,
  registerWebinarLead,
  resumeWebinarLead,
  storeToken,
  trackWebinar,
  type WebinarConfig,
  type WebinarLead,
} from "@/services/webinar";

const COUNTRIES: { name: string; iso?: string; dial: string; tz?: string[] }[] = [
  { name: "Colombia", iso: "CO", dial: "57", tz: ["America/Bogota"] },
  { name: "Estados Unidos", iso: "US", dial: "1", tz: ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix"] },
  { name: "México", iso: "MX", dial: "52", tz: ["America/Mexico_City", "America/Monterrey", "America/Tijuana"] },
  { name: "España", iso: "ES", dial: "34", tz: ["Europe/Madrid"] },
  { name: "Argentina", iso: "AR", dial: "54", tz: ["America/Argentina/Buenos_Aires", "America/Buenos_Aires"] },
  { name: "Chile", iso: "CL", dial: "56", tz: ["America/Santiago"] },
  { name: "Perú", iso: "PE", dial: "51", tz: ["America/Lima"] },
  { name: "Ecuador", iso: "EC", dial: "593", tz: ["America/Guayaquil"] },
  { name: "Venezuela", iso: "VE", dial: "58", tz: ["America/Caracas"] },
  { name: "Panamá", iso: "PA", dial: "507", tz: ["America/Panama"] },
  { name: "Costa Rica", iso: "CR", dial: "506", tz: ["America/Costa_Rica"] },
  { name: "República Dominicana", iso: "DO", dial: "1809", tz: ["America/Santo_Domingo"] },
  { name: "Guatemala", iso: "GT", dial: "502", tz: ["America/Guatemala"] },
  { name: "Honduras", iso: "HN", dial: "504", tz: ["America/Tegucigalpa"] },
  { name: "El Salvador", iso: "SV", dial: "503", tz: ["America/El_Salvador"] },
  { name: "Bolivia", iso: "BO", dial: "591", tz: ["America/La_Paz"] },
  { name: "Paraguay", iso: "PY", dial: "595", tz: ["America/Asuncion"] },
  { name: "Uruguay", iso: "UY", dial: "598", tz: ["America/Montevideo"] },
  { name: "Brasil", iso: "BR", dial: "55", tz: ["America/Sao_Paulo"] },
  { name: "Otro país", dial: "" },
];

const PATHS = [
  { label: "Crece", icon: "ri-seedling-line", color: "#20C997" },
  { label: "Emprende", icon: "ri-lightbulb-flash-line", color: "#FACC15" },
  { label: "Vende", icon: "ri-hand-coin-line", color: "#60A5FA" },
  { label: "Lidera", icon: "ri-vip-crown-2-line", color: "#A78BFA" },
];

const BENEFITS = [
  { icon: "ri-compass-3-line", title: "Descubre tu etapa", text: "Entiende dónde estás hoy y qué te frena." },
  { icon: "ri-fingerprint-line", title: "Conoce tu perfil", text: "Tu estilo para comunicar, liderar y construir confianza." },
  { icon: "ri-route-line", title: "Recibe tu ruta", text: "Un camino claro con tu siguiente paso." },
];

const fieldCls = "w-full px-3.5 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/40 text-sm outline-none focus:border-[#20C997] transition-colors";

function guessCountry(): number {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const i = COUNTRIES.findIndex((c) => c.tz?.includes(tz));
  return i >= 0 ? i : 0;
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["es"], { type: "region" });
  } catch {
    return null;
  }
})();
const flagOf = (iso: string) => iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
const NOT_MOBILE = ["FIXED_LINE", "TOLL_FREE", "PREMIUM_RATE", "SHARED_COST", "VOIP", "PAGER", "UAN", "VOICEMAIL"];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Avance de quien aún no dejó su WhatsApp (solo en este navegador); se sube al registrarse.
const ANON_KEY = "vv-webinar-anon";
type Anon = { max: number; pos: number };
const readAnon = (): Anon => {
  try {
    const j = JSON.parse(window.localStorage.getItem(ANON_KEY) || "{}");
    return { max: Number(j.max) || 0, pos: Number(j.pos) || 0 };
  } catch {
    return { max: 0, pos: 0 };
  }
};
const writeAnon = (a: Anon) => {
  try {
    window.localStorage.setItem(ANON_KEY, JSON.stringify(a));
  } catch {
    // sin almacenamiento
  }
};
const clearAnon = () => {
  try {
    window.localStorage.removeItem(ANON_KEY);
  } catch {
    // sin almacenamiento
  }
};

type PhoneCheck = { ok: boolean; error: string; digits: string; iso: string; countryName: string; pretty: string };
const BAD = (error: string): PhoneCheck => ({ ok: false, error, digits: "", iso: "", countryName: "", pretty: "" });

/** Valida formato, país y que sea un móvil. Acepta el número nacional, con código o con "+". */
function checkPhone(dial: string, raw: string, selectedIdx: number): PhoneCheck {
  const typed = raw.trim();
  const code = dial.replace(/\D/g, "");
  const national = typed.replace(/\D/g, "").replace(/^0+/, "");
  let candidate: string;
  if (typed.startsWith("+")) candidate = `+${typed.replace(/\D/g, "")}`;
  else if (code && national.startsWith(code) && national.length >= code.length + 8) candidate = `+${national}`;
  else candidate = `+${code}${national}`;
  const p = parsePhoneNumberFromString(candidate);
  const label = COUNTRIES[selectedIdx]?.name;
  if (!p || !p.isValid()) return BAD(`Ese número no parece válido${label && code ? ` en ${label}` : ""}. Revísalo.`);
  const type = p.getType();
  if (type && NOT_MOBILE.includes(type)) return BAD("Ese número parece de línea fija. Usa tu celular con WhatsApp.");
  const iso = p.country ?? "";
  const countryName = (iso && regionNames?.of(iso)) || label || "";
  return { ok: true, error: "", digits: p.number.replace(/\D/g, ""), iso, countryName, pretty: p.formatInternational() };
}

function RegisterForm({ ref_, src, onDone, cta }: { ref_: string | null; src: string | null; onDone: (lead: WebinarLead) => void; cta: string }) {
  const [name, setName] = useState("");
  const [countryIdx, setCountryIdx] = useState(guessCountry);
  const [dial, setDial] = useState(COUNTRIES[guessCountry()].dial);
  const [number, setNumber] = useState("");
  const [consent, setConsent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leadRef = useRef<WebinarLead | null>(null);

  const live = useMemo(() => (number.replace(/\D/g, "").length >= 7 ? checkPhone(dial, number, countryIdx) : null), [dial, number, countryIdx]);
  const mismatch = live?.ok && COUNTRIES[countryIdx].iso && live.iso && live.iso !== COUNTRIES[countryIdx].iso && dial !== "1";

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    if (((data.get("website_alt") as string) || "").trim()) return; // anti-bots
    if (name.trim().length < 2) return setError("Escribe tu nombre.");
    const chk = checkPhone(dial, number, countryIdx);
    if (!chk.ok) return setError(chk.error);
    setBusy(true);
    try {
      let lead = leadRef.current;
      if (!lead) {
        lead = await registerWebinarLead({ ref: ref_, name: name.trim(), phone: chk.digits, country: chk.countryName, consent, src });
        leadRef.current = lead;
      } else {
        await updateWebinarPhone(lead.token, chk.digits, chk.countryName);
      }
      // Validación en vivo: se envía un WhatsApp de confirmación y se espera unos segundos el resultado.
      const v = await verifyWebinarPhone(lead.token);
      if (v.status === "invalid") {
        setError(
          v.reason === "no_whatsapp"
            ? "No encontramos WhatsApp en ese número. Revísalo o usa otro."
            : v.reason === "not_mobile"
              ? "Ese número parece de línea fija. Usa tu celular con WhatsApp."
              : "Ese número no parece válido. Revísalo.",
        );
        return;
      }
      if (v.status === "sent") {
        setSending(true);
        for (let i = 0; i < 5; i++) {
          await sleep(1000);
          const st = await fetchPhoneStatus(lead.token);
          if (st.status === "delivered") break;
          if (st.status === "invalid") {
            setError("No encontramos WhatsApp en ese número. Revísalo o usa otro.");
            return;
          }
        }
      }
      onDone(lead);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos registrarte. Intenta de nuevo.");
    } finally {
      setBusy(false);
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" autoComplete="given-name" maxLength={80} className={fieldCls} aria-label="Tu nombre" />
      <div className="grid grid-cols-[8.5rem_1fr] gap-2">
        <select
          value={countryIdx}
          onChange={(e) => {
            const i = Number(e.target.value);
            setCountryIdx(i);
            setDial(COUNTRIES[i].dial);
          }}
          className={`${fieldCls} appearance-none`}
          aria-label="País"
        >
          {COUNTRIES.map((c, i) => (
            <option key={c.name} value={i} className="text-slate-900">
              {c.name}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1.5">
          <span className="text-white/60 text-sm pl-1">+</span>
          <input value={dial} onChange={(e) => setDial(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" className={`${fieldCls} !w-16 !px-2 text-center`} aria-label="Código de país" />
          <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Tu WhatsApp" inputMode="tel" autoComplete="tel-national" className={fieldCls} aria-label="Tu WhatsApp" />
        </div>
      </div>
      {live?.ok ? (
        <p className="text-xs text-[#20C997] -mt-1">
          <i className="ri-checkbox-circle-line"></i> {live.iso ? `${flagOf(live.iso)} ` : ""}
          {live.countryName} · {live.pretty}
          {mismatch ? " (detectamos el país por tu número)" : ""}
        </p>
      ) : null}
      <input name="website_alt" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <label className="flex items-start gap-2.5 text-xs text-white/70 leading-snug cursor-pointer">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#20C997]" />
        <span>
          Quiero recibir por WhatsApp el enlace del webinar y un recordatorio si me quedo a medias. Puedo responder <strong className="text-white">STOP</strong> en cualquier momento.
        </span>
      </label>
      {error ? <p className="text-sm text-rose-300">{error}</p> : null}
      {sending ? (
        <p className="text-xs text-white/70 flex items-center gap-2">
          <i className="ri-whatsapp-line text-[#20C997]"></i> Te estamos escribiendo por WhatsApp para confirmar tu número…
        </p>
      ) : null}
      <button type="submit" disabled={busy} className="mt-1 w-full py-3.5 rounded-xl bg-[#20C997] hover:bg-[#1BB588] disabled:opacity-60 text-[#0B1F3A] font-heading font-extrabold text-sm cursor-pointer whitespace-nowrap transition-colors">
        {busy ? (sending ? "Confirmando…" : "Un momento…") : cta}
      </button>
    </form>
  );
}
export default function Webinar() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = (params.get("ref") || params.get("r") || "").trim().toLowerCase() || null;
  const resumeToken = (params.get("t") || "").trim() || null;
  const src = (params.get("src") || params.get("s") || "").trim().toLowerCase().slice(0, 30) || null;

  useEffect(() => {
    void trackProspectVisit();
  }, []);

  const [config, setConfig] = useState<WebinarConfig | null>(null);
  const [lead, setLead] = useState<WebinarLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [modal, setModal] = useState<null | "test" | "community">(null);
  const [ended, setEnded] = useState(false);
  const [seed, setSeed] = useState({ startAt: 0, percent: 0 });
  const [gateOpen, setGateOpen] = useState(false);
  const [resumeKey, setResumeKey] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const cfg = await fetchWebinarConfig(ref);
        if (!active) return;
        setConfig(cfg);
        const token = resumeToken || getStoredToken();
        let resolved = false;
        if (token) {
          const l = await resumeWebinarLead(token);
          if (!active) return;
          if (l) {
            resolved = true;
            setLead(l);
            storeToken(l.token);
            setEnded(l.completed);
            setSeed({ startAt: !l.completed && l.position > 5 ? l.position : 0, percent: l.maxPercent });
          } else if (!resumeToken) storeToken(null);
        }
        if (!resolved) {
          const an = readAnon();
          if (an.max > 0) setSeed({ startAt: an.pos > 5 ? an.pos : 0, percent: an.max });
          if (cfg.captureMode === "mid_video" && cfg.videoUrl && an.max >= cfg.captureAtPercent) setGateOpen(true);
        }
      } catch {
        if (active) setFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [ref, resumeToken]);

  const token = lead?.token ?? null;
  const onStart = useCallback(() => token && void trackWebinar(token, "video_started", 0, 0), [token]);
  const onProgress = useCallback(
    (pct: number, pos: number) => {
      if (token) {
        void trackWebinar(token, "progress", pct, pos);
        return;
      }
      const a = readAnon();
      writeAnon({ max: Math.max(a.max, Math.round(pct)), pos: Math.round(pos) });
    },
    [token],
  );
  const onGate = useCallback(() => setGateOpen(true), []);

  /** Al dejar el WhatsApp: se guarda lo que ya vio como anónimo y el video continúa. */
  const adoptLead = (l: WebinarLead, resume: boolean) => {
    const a = readAnon();
    setLead(l);
    setEnded(l.completed);
    if (a.max > 0) {
      void trackWebinar(l.token, "video_started", 0, a.pos);
      void trackWebinar(l.token, "progress", a.max, a.pos);
    }
    clearAnon();
    setGateOpen(false);
    if (resume) setResumeKey((k) => k + 1);
  };
  const onEnded = useCallback(() => {
    setEnded(true);
    if (token) void trackWebinar(token, "video_completed", 100);
  }, [token]);

  const goNext = (kind: "test" | "community", l: WebinarLead | null) => {
    if (l) void trackWebinar(l.token, kind === "test" ? "cta_test_clicked" : "cta_community_clicked");
    const code = config?.signupRef;
    navigate(code ? `/registro/${code}` : "/");
  };

  const onCta = (kind: "test" | "community") => {
    if (lead) goNext(kind, lead);
    else setModal(kind);
  };

  const needsRegistration = config?.captureMode === "before_video" && !lead;
  const gateAt = config?.captureMode === "mid_video" && !lead ? config.captureAtPercent : null;
  const firstName = lead?.name.split(" ")[0];

  return (
    <div className="min-h-screen bg-[#060B26] text-white relative overflow-x-hidden">
      <div className="pointer-events-none absolute -top-40 -right-32 w-[34rem] h-[34rem] rounded-full bg-gradient-to-bl from-[#7C3AED]/40 via-[#20C997]/20 to-transparent blur-3xl"></div>
      <div className="pointer-events-none absolute top-[40rem] -left-40 w-[30rem] h-[30rem] rounded-full bg-[#20C997]/10 blur-3xl"></div>

      <header className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 pt-[calc(env(safe-area-inset-top)+1rem)] pb-2 flex items-center justify-between">
        <Logo size="md" variant="onDark" />
        <span className="text-[11px] font-bold tracking-wide uppercase text-[#20C997] bg-[#20C997]/10 border border-[#20C997]/30 rounded-full px-3 py-1">Webinar gratuito</span>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 pb-16">
        {loading ? (
          <div className="py-32 text-center text-white/60">
            <i className="ri-loader-4-line animate-spin text-3xl"></i>
          </div>
        ) : failed || !config?.enabled ? (
          <div className="py-24 text-center max-w-md mx-auto">
            <i className="ri-calendar-close-line text-4xl text-white/50"></i>
            <h1 className="font-heading text-2xl font-extrabold mt-3">Este webinar no está disponible</h1>
            <p className="text-white/60 text-sm mt-2">Vuelve a intentarlo más tarde o pide a quien te invitó que te comparta el enlace de nuevo.</p>
          </div>
        ) : (
          <>
            <section className="text-center pt-6 md:pt-10 max-w-3xl mx-auto">
              {config.sponsorName ? <p className="text-sm text-white/60 mb-3">Te invita <span className="font-semibold text-white">{config.sponsorName}</span></p> : null}
              <h1 className="font-heading font-extrabold text-4xl md:text-6xl leading-[1.05] tracking-tight">{config.title}</h1>
              <p className="mt-4 text-white/70 md:text-lg">{config.subtitle}</p>
              {firstName && lead ? (
                <p className="mt-4 inline-flex items-center gap-2 text-sm bg-white/5 border border-white/10 rounded-full px-4 py-1.5">
                  <i className="ri-user-smile-line text-[#20C997]"></i>
                  {lead.maxPercent > 0 && !lead.completed ? `Hola ${firstName}, retomamos tu webinar donde lo dejaste (${lead.maxPercent}% visto).` : `Hola ${firstName}, ¡bienvenida/o!`}
                </p>
              ) : null}
            </section>

            <section className="mt-8 md:mt-10 grid gap-6 lg:grid-cols-[1fr_22rem] items-start">
              <div>
                {needsRegistration || !config.videoUrl ? (
                  <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0B1F3A] via-[#13214a] to-[#2a1b5e] flex items-center justify-center shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
                    <div className="text-center px-6">
                      <span className="w-16 h-16 mx-auto rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                        <i className={`${needsRegistration ? "ri-lock-2-line" : "ri-time-line"} text-3xl text-white/80`}></i>
                      </span>
                      <p className="mt-3 font-heading font-bold">{needsRegistration ? "Regístrate para ver el webinar" : "El video se publicará muy pronto"}</p>
                      <p className="text-xs text-white/60 mt-1">{needsRegistration ? "Solo toma 20 segundos. Es gratis." : "Mientras tanto, conoce lo que viene abajo."}</p>
                    </div>
                  </div>
                ) : (
                  <WebinarPlayer
                    key={config.videoUrl}
                    url={config.videoUrl}
                    duration={config.videoDuration}
                    initialPercent={seed.percent}
                    startAt={seed.startAt}
                    gateAtPercent={gateAt}
                    onGate={onGate}
                    resumeKey={resumeKey}
                    onStart={onStart}
                    onProgress={onProgress}
                    onEnded={onEnded}
                  />
                )}
              </div>

              {needsRegistration ? (
                <aside className="rounded-2xl bg-[#0B1330]/90 border border-white/10 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.35)]" id="registro">
                  <h2 className="font-heading font-bold text-lg">Reserva tu lugar</h2>
                  <p className="text-sm text-white/60 mt-1 mb-4">Déjanos tu WhatsApp y te enviamos el enlace para retomarlo cuando quieras.</p>
                  <RegisterForm ref_={ref} src={src} onDone={(l) => adoptLead(l, false)} cta="Ver el webinar ahora" />
                </aside>
              ) : (
                <aside className="rounded-2xl bg-[#0B1330]/90 border border-white/10 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
                  <h2 className="font-heading font-bold text-lg">{ended ? "¡Terminaste el webinar! 🎉" : "¿Y ahora qué?"}</h2>
                  <p className="text-sm text-white/60 mt-1 mb-4">{ended ? "Elige tu siguiente paso. Sin presión: tú decides." : "Cuando quieras, da el siguiente paso. Puedes verlo antes o después."}</p>
                  <div className="flex flex-col gap-3">
                    <button onClick={() => onCta("test")} className="w-full py-3.5 rounded-xl bg-[#20C997] hover:bg-[#1BB588] text-[#0B1F3A] font-heading font-extrabold text-sm cursor-pointer transition-colors">
                      <i className="ri-fingerprint-line mr-1.5"></i>Descubrir mi perfil
                    </button>
                    <button onClick={() => onCta("community")} className="w-full py-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/20 text-white font-heading font-bold text-sm cursor-pointer transition-colors">
                      <i className="ri-community-line mr-1.5"></i>Conocer la comunidad
                    </button>
                  </div>
                </aside>
              )}
            </section>

            <section className="mt-12 grid gap-3 sm:grid-cols-3">
              {BENEFITS.map((b) => (
                <div key={b.title} className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
                  <span className="w-10 h-10 rounded-xl bg-[#20C997]/15 text-[#20C997] flex items-center justify-center">
                    <i className={`${b.icon} text-xl`}></i>
                  </span>
                  <h3 className="font-heading font-bold mt-3">{b.title}</h3>
                  <p className="text-sm text-white/60 mt-1">{b.text}</p>
                </div>
              ))}
            </section>

            <section className="mt-10">
              <h2 className="text-center font-heading font-bold text-lg">Los 4 caminos de Vívelo</h2>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {PATHS.map((p) => (
                  <div key={p.label} className="rounded-2xl bg-white/[0.04] border border-white/10 py-4 text-center">
                    <span className="w-11 h-11 mx-auto rounded-full flex items-center justify-center" style={{ color: p.color, backgroundColor: `${p.color}22` }}>
                      <i className={`${p.icon} text-xl`}></i>
                    </span>
                    <div className="font-heading font-bold mt-2">{p.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <p className="mt-12 text-center text-[11px] text-white/40 max-w-xl mx-auto leading-relaxed">
              Vívelo AI es una plataforma educativa y de acompañamiento. No prometemos ingresos ni resultados garantizados: los resultados dependen de tu constancia y de tus decisiones.
            </p>
          </>
        )}
      </main>

      {gateOpen && !lead ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Guarda tu avance">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm"></div>
          <div className="relative w-full max-w-sm rounded-2xl bg-[#0B1330] border border-white/10 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
            <span className="w-11 h-11 rounded-full bg-[#20C997]/15 text-[#20C997] flex items-center justify-center">
              <i className="ri-bookmark-3-line text-xl"></i>
            </span>
            <h2 className="font-heading font-bold text-lg mt-3">¡Vas muy bien! Guarda tu avance</h2>
            <p className="text-sm text-white/60 mt-1 mb-4">Deja tu WhatsApp, te confirmamos al instante y sigues viendo el webinar justo donde lo dejaste.</p>
            <RegisterForm ref_={ref} src={src} cta="Guardar y seguir viendo" onDone={(l) => adoptLead(l, true)} />
          </div>
        </div>
      ) : null}

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Registro">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setModal(null)}></div>
          <div className="relative w-full max-w-sm rounded-2xl bg-[#0B1330] border border-white/10 p-5">
            <button onClick={() => setModal(null)} aria-label="Cerrar" className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-white/10 cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
            <h2 className="font-heading font-bold text-lg pr-8">Un paso antes de continuar</h2>
            <p className="text-sm text-white/60 mt-1 mb-4">Confirma tu nombre y WhatsApp para guardar tu avance.</p>
            <RegisterForm
              ref_={ref}
              src={src}
              cta="Continuar"
              onDone={(l) => {
                adoptLead(l, false);
                const kind = modal;
                setModal(null);
                goNext(kind, l);
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
